import { ref } from 'vue'

export default function useFileHandler() {
	const isLoading = ref(false)
	const error = ref<Error | null>(null)
	const objectUrl = ref<string | null>(null)

	/**
	 * Download any file from a URL
	 * @param fileUrl The URL of the file to download
	 * @param filename The name to save the file as
	 */
	const downloadFile = async (
		fileUrl: string,
		filename: string
	): Promise<void> => {
		isLoading.value = true
		error.value = null

		try {
			// Create a link element
			const link = document.createElement('a')
			link.href = fileUrl
			link.download = filename
			link.target = '_blank'

			// Append to the document, click it, and remove it
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			isLoading.value = false
		} catch (err) {
			error.value =
				err instanceof Error ? err : new Error('Failed to download file')
			isLoading.value = false
			throw error.value
		}
	}

	/**
	 * Download file from Blob or File object
	 * @param file The File or Blob to download
	 * @param filename The name to save the file as
	 */
	const downloadBlob = (file: File | Blob, filename: string): void => {
		isLoading.value = true
		error.value = null

		try {
			// Revoke previous URL if it exists
			if (objectUrl.value) {
				URL.revokeObjectURL(objectUrl.value)
			}

			// Create new URL
			const url = URL.createObjectURL(file)
			objectUrl.value = url

			// Create download link
			const link = document.createElement('a')
			link.href = url
			link.download = filename

			// Trigger download
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			// Clean up the URL after a short delay
			setTimeout(() => {
				URL.revokeObjectURL(url)
				objectUrl.value = null
			}, 100)

			isLoading.value = false
		} catch (err) {
			error.value =
				err instanceof Error ? err : new Error('Failed to download file')
			isLoading.value = false
			throw error.value
		}
	}

	/**
	 * Fetch and download a file (useful when CORS is an issue)
	 * @param fileUrl The URL of the file to download
	 * @param filename The name to save the file as
	 * @param options Optional fetch options
	 */
	const fetchAndDownload = async (
		fileUrl: string,
		filename: string,
		options?: RequestInit
	): Promise<void> => {
		isLoading.value = true
		error.value = null

		try {
			// Fetch the file
			const response = await fetch(fileUrl, options)

			if (!response.ok) {
				throw new Error(
					`Failed to fetch file: ${response.status} ${response.statusText}`
				)
			}

			// Get the blob
			const blob = await response.blob()

			// Download the blob
			downloadBlob(blob, filename)
		} catch (err) {
			error.value =
				err instanceof Error
					? err
					: new Error('Failed to fetch and download file')
			isLoading.value = false
			throw error.value
		}
	}

	const createBlobUrl = (file: File | Blob): string => {
		// Revoke previous URL if it exists
		if (objectUrl.value) {
			URL.revokeObjectURL(objectUrl.value)
		}

		// Create new URL
		const url = URL.createObjectURL(file)
		objectUrl.value = url
		return url
	}

	/**
	 * Print PDF from a File or Blob object using iframe only
	 */
	const printPdf = (file: File | Blob): void => {
		try {
			isLoading.value = true
			error.value = null

			// Create blob URL
			const url = createBlobUrl(file)

			// Create a hidden div to hold our print content
			const printContainer = document.createElement('div')
			printContainer.style.position = 'fixed'
			printContainer.style.left = '-9999px'
			printContainer.style.top = '-9999px'
			document.body.appendChild(printContainer)

			// Create the iframe with proper attributes for PDF viewing
			const printFrame = document.createElement('iframe')
			printFrame.style.width = '1000px'
			printFrame.style.height = '1000px'
			printFrame.style.border = '0'

			// Set PDF-specific attributes
			printFrame.setAttribute('src', url)
			printFrame.setAttribute('type', 'application/pdf')

			// Add the iframe to our container
			printContainer.appendChild(printFrame)

			// Track whether print has been triggered
			let printTriggered = false

			// Function to trigger print with proper timing
			const triggerPrint = () => {
				if (printTriggered) return

				try {
					if (printFrame.contentWindow) {
						const beforePrint = () => {
							isLoading.value = true
						}

						const afterPrint = () => {
							document.body.removeChild(printContainer)

							window.removeEventListener('beforeprint', beforePrint)
							window.removeEventListener('afterprint', afterPrint)

							isLoading.value = false
						}

						// Add event listeners for print dialog
						window.addEventListener('beforeprint', beforePrint)
						window.addEventListener('afterprint', afterPrint)

						// Trigger the print dialog
						printFrame.contentWindow.print()
						printTriggered = true

						// Fallback cleanup in case afterprint doesn't fire
						// This happens in some browsers in certain conditions
						setTimeout(() => {
							if (isLoading.value) {
								console.log('Fallback cleanup triggered')
								document.body.removeChild(printContainer)

								// Remove the event listeners
								window.removeEventListener('beforeprint', beforePrint)
								window.removeEventListener('afterprint', afterPrint)

								isLoading.value = false
							}
						}, 60000) // 1 minute timeout as a safety net
					}
				} catch (err) {
					console.error('Print error:', err)
					document.body.removeChild(printContainer)
					error.value =
						err instanceof Error ? err : new Error('Failed to print PDF')
					isLoading.value = false
				}
			}

			// Set up the load event
			printFrame.onload = () => {
				// Give it a moment to render the PDF after load event
				setTimeout(triggerPrint, 1000)
			}

			// Fallback: Check readyState periodically
			const checkReadyState = setInterval(() => {
				try {
					if (
						printFrame.contentDocument &&
						printFrame.contentDocument.readyState === 'complete'
					) {
						clearInterval(checkReadyState)

						if (!printTriggered) {
							setTimeout(triggerPrint, 500)
						}
					}
				} catch (e) {
					// CORS issues might prevent accessing contentDocument
				}
			}, 500)

			// Safety timeout
			setTimeout(() => {
				if (!printTriggered) {
					triggerPrint()
				}
				clearInterval(checkReadyState)
			}, 3000)
		} catch (err) {
			console.error('General error:', err)
			error.value =
				err instanceof Error ? err : new Error('Failed to print PDF')
			isLoading.value = false
		}
	}
	/**
	 * Clean up resources
	 */
	const cleanup = (): void => {
		if (objectUrl.value) {
			URL.revokeObjectURL(objectUrl.value)
			objectUrl.value = null
		}
	}

	return {
		isLoading,
		error,
		downloadFile,
		downloadBlob,
		fetchAndDownload,
		printPdf,
		cleanup,
	}
}
